import { Table as AntdTable, Tabs as AntdTabs, Flex } from 'antd';
import styled from 'styled-components';

import { CSS_PREFIX } from '@/constants/styles';
import {
  BlueColors,
  GreenColors,
  NeutralColors,
  RedColors,
  YellowColors
} from '@/theme';

import { ROW_HIGHLIGHT_CLASS } from './constants';

export const Root = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

export const OuterScroll = styled(Flex)<{ $virtual?: boolean }>`
  width: 100%;
  overflow-x: hidden;
  overflow-y: ${props => (props.$virtual ? 'hidden' : 'auto')};
  -webkit-overflow-scrolling: touch;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

export const TableContainer = styled.div`
  flex: 1;
  position: relative;
`;

export const Table = styled(AntdTable)`
  width: 100%;
  --rc-virtual-list-scrollbar-bg: ${NeutralColors[50]};

  /* AntD virtual table re-prefixes rc-virtual-list, so the scrollbar class
     becomes \`\${tablePrefix}-tbody-virtual-scrollbar-*\`. Match by suffix so
     this works across AntD versions and without depending on CSS_PREFIX. */
  [class$='-scrollbar-vertical'] {
    visibility: visible !important;
    width: 12px !important;
  }

  [class$='-scrollbar-horizontal'] {
    visibility: visible !important;
    height: 12px !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--info
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--info
    .${CSS_PREFIX}-table-cell {
    background-color: ${BlueColors[10]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--success
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--success
    .${CSS_PREFIX}-table-cell {
    background-color: ${GreenColors[10]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--warning
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--warning
    .${CSS_PREFIX}-table-cell {
    background-color: ${YellowColors[10]} !important;
    border-bottom: 1px solid ${YellowColors[30]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--danger
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--danger
    .${CSS_PREFIX}-table-cell {
    background-color: ${RedColors[10]} !important;
    color: ${RedColors[50]} !important;
    font-weight: 500 !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--info:hover
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--info:hover
    .${CSS_PREFIX}-table-cell {
    background-color: ${BlueColors[20]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--success:hover
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--success:hover
    .${CSS_PREFIX}-table-cell {
    background-color: ${GreenColors[20]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--warning:hover
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--warning:hover
    .${CSS_PREFIX}-table-cell {
    background-color: ${YellowColors[20]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--danger:hover
    > .${CSS_PREFIX}-table-cell,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--danger:hover
    .${CSS_PREFIX}-table-cell {
    background-color: ${RedColors[20]} !important;
    color: ${RedColors[60]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--info.${ROW_HIGHLIGHT_CLASS}--marker
    > .${CSS_PREFIX}-table-cell:first-child,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--info.${ROW_HIGHLIGHT_CLASS}--marker
    .${CSS_PREFIX}-table-cell:first-child {
    box-shadow: inset 4px 0 0 0 ${BlueColors[50]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--success.${ROW_HIGHLIGHT_CLASS}--marker
    > .${CSS_PREFIX}-table-cell:first-child,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--success.${ROW_HIGHLIGHT_CLASS}--marker
    .${CSS_PREFIX}-table-cell:first-child {
    box-shadow: inset 4px 0 0 0 ${GreenColors[50]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--warning.${ROW_HIGHLIGHT_CLASS}--marker
    > .${CSS_PREFIX}-table-cell:first-child,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--warning.${ROW_HIGHLIGHT_CLASS}--marker
    .${CSS_PREFIX}-table-cell:first-child {
    box-shadow: inset 4px 0 0 0 ${YellowColors[50]} !important;
  }

  tr.${ROW_HIGHLIGHT_CLASS}--danger.${ROW_HIGHLIGHT_CLASS}--marker
    > .${CSS_PREFIX}-table-cell:first-child,
    .${CSS_PREFIX}-table-row.${ROW_HIGHLIGHT_CLASS}--danger.${ROW_HIGHLIGHT_CLASS}--marker
    .${CSS_PREFIX}-table-cell:first-child {
    box-shadow: inset 4px 0 0 0 ${RedColors[50]} !important;
  }

  .table-cell-highlight--info {
    background-color: ${BlueColors[10]} !important;
  }

  .table-cell-highlight--success {
    background-color: ${GreenColors[10]} !important;
  }

  .table-cell-highlight--warning {
    background-color: ${YellowColors[10]} !important;
  }

  .table-cell-highlight--danger {
    background-color: ${RedColors[10]} !important;
  }
` as typeof AntdTable;

export const TableContent = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  top: 0;
  right: 0;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  height: 75px;
  background-color: ${NeutralColors[0]};
  border-top: 1px solid ${NeutralColors[20]};
`;

export const Header = styled.div`
  padding: 0 16px;
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  background: white;
  gap: 8px;
  overflow-x: auto;
`;

export const HeaderActions = styled(Flex)`
  .${CSS_PREFIX}-btn-icon-only {
    border-radius: 4px;
  }
`;

export const LeftMenuContainer = styled(Flex)`
  position: relative;
  display: flex;
  flex: 1;
`;

export const LeftMenuContent = styled(Flex)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-x: auto;
`;

export const SearchContainer = styled.div<{ $maxWidth?: number }>`
  display: flex;
  flex: 1;
  align-items: center;

  .${CSS_PREFIX}-input-affix-wrapper {
    width: ${({ $maxWidth }) => $maxWidth ?? 550}px;
    max-width: 100%;
    background-color: ${NeutralColors[10]};
  }
`;

export const FilterTagsRow = styled.div`
  padding: 0 16px;
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-x: auto;
`;

export const TabsWrapper = styled(AntdTabs)`
  padding: 0 12px;

  .panorama-tabs-nav {
    margin-bottom: 0;
  }
`;
